import {
  type OrthographicCamera,
  PCFShadowMap,
  PerspectiveCamera,
  WebGLRenderer
} from "three";

import { PostProcessing } from "./PostProcessing";
import { engineUniforms } from "./materials/engineUniforms";
import type { World } from "./world/World";

export type FrameCallback = (deltaSeconds: number) => void;

function checkForSoftwareRenderer(webgl: WebGLRenderer) {
  const gl = webgl.getContext();
  const info = gl.getExtension("WEBGL_debug_renderer_info");
  const gpu = gl.getParameter(info?.UNMASKED_RENDERER_WEBGL ?? gl.RENDERER);
  return /llvmpipe|swiftshader|software/i.test(String(gpu));
}

export class Renderer {
  readonly webgl = new WebGLRenderer({ powerPreference: "high-performance" });
  private readonly postProcessing: PostProcessing;
  private readonly resizeObserver: ResizeObserver;
  private readonly frameCallbacks = new Set<FrameCallback>();
  private lastFrameTime: number | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly world: World,
    private readonly camera: OrthographicCamera | PerspectiveCamera
  ) {
    const isSoftwareRenderer = checkForSoftwareRenderer(this.webgl);
    this.webgl.setPixelRatio(
      Math.min(window.devicePixelRatio, 2) * (isSoftwareRenderer ? 0.75 : 1)
    );
    this.webgl.shadowMap.enabled = !isSoftwareRenderer;
    this.webgl.shadowMap.type = PCFShadowMap;
    container.appendChild(this.webgl.domElement);

    this.postProcessing = new PostProcessing(
      this.webgl,
      world.scene,
      camera,
      isSoftwareRenderer
    );

    this.resizeObserver = new ResizeObserver(() => this.resizeToContainer());
    this.resizeObserver.observe(container);
    this.resizeToContainer();

    this.webgl.setAnimationLoop((time) => this.renderFrame(time));
  }

  get canvas(): HTMLCanvasElement {
    return this.webgl.domElement;
  }

  setBloomStrength(strength: number) {
    this.postProcessing.setBloomStrength(strength);
  }

  onFrame(callback: FrameCallback): () => void {
    this.frameCallbacks.add(callback);
    return () => this.frameCallbacks.delete(callback);
  }

  dispose() {
    this.webgl.setAnimationLoop(null);
    this.resizeObserver.disconnect();
    this.frameCallbacks.clear();
    this.postProcessing.dispose();
    this.webgl.dispose();
    this.webgl.forceContextLoss();
    this.webgl.domElement.remove();
  }

  private renderFrame(time: number) {
    const deltaSeconds =
      this.lastFrameTime === null ? 0 : (time - this.lastFrameTime) / 1000;
    this.lastFrameTime = time;
    engineUniforms.uTime.value = time / 1000;

    for (const callback of this.frameCallbacks) callback(deltaSeconds);
    this.world.step(deltaSeconds);
    this.postProcessing.render(deltaSeconds, this.world.getObjects());
  }

  private resizeToContainer() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.webgl.setSize(width, height);
    this.postProcessing.setSize(width, height);
    fitCameraToAspect(this.camera, width / height);
  }
}

function fitCameraToAspect(
  camera: OrthographicCamera | PerspectiveCamera,
  aspect: number
) {
  if (camera instanceof PerspectiveCamera) {
    camera.aspect = aspect;
  } else {
    camera.left = camera.bottom * aspect;
    camera.right = camera.top * aspect;
  }
  camera.updateProjectionMatrix();
}
